<template>
	<div class="p4">
		<!-- Reject Reason -->
		<core-form-input
			id="rejectedReason"
			componentName="j-base-multi-select"
			name="rejectedReason"
			:label="$t('rejected_reason')"
			:options="rejectionOptions"
			v-model="rejectionReasons"
			:allowLocalSearch="true"
		/>

		<!-- Remarks/Comments -->
		<core-form-input
			componentName="j-base-input-long-text"
			id="rejectionRemarks"
			name="rejectionRemarks"
			class="mt8"
			:label="this.$t('remarks')"
			v-model="rejectionRemarks"
			v-validate="'required'"
		/>

		<!-- Toggle for sending cancellation email to client -->
		<core-form-input
			componentName="j-base-radios"
			name="sendEmailToggle"
			class="mt8"
			:label="this.$t('send_rejection_email')"
			v-model="sendEmailToggle"
			:options="[
				{ name: $t('yes'), value: 'yes' },
				{ name: $t('no'), value: 'no' }
			]"
		/>
		<j-button
			class="mt8"
			type="error"
			:isDisabled="isLoading"
			:isLoading="isLoading"
			@click="onRejectProposal"
			>{{ this.$t("reject") }}</j-button
		>
	</div>
</template>

<script>
import CoreFormInput from "@/modules/core/components/CoreFormInput"
import JButton from "@/components/JButton"
import api from "@/api/gql.js"
import get from "lodash.get"
export default {
	components: {
		CoreFormInput,
		JButton
	},
	props: {
		payload: {
			type: Object,
			required: true
		},
		id: {
			type: String,
			required: true
		}
	},
	data() {
		return {
			isLoading: false,
			sendEmailToggle: "yes", // toggle to send email to client or not
			rejectionRemarks: null, // remarks inputed by user
			rejectionReasons: [], // reasons selected by user
			rejectionOptions: [] // list of options that user can select as reason
		}
	},
	computed: {
		env() {
			return this.$store.state.settings.env
		},
		caseId() {
			return this.$store.state.caseManagement.caseId
		},
		case() {
			return this.$store.state.caseManagement.case
		},
		proposalId() {
			return this.$store.state.caseManagement.proposalId
		},
		offerId() {
			const offer = this.$store.state.caseManagement.proposals.find(
				item => item.id === this.proposalId
			)
			return offer.id
		}
	},
	methods: {
		// initalize rejectionRemarks and rejectioReasons with data from payload
		// used to update rejection info but also populating fields with existing data
		initialize() {
			const updatedRemarks = this.payload.rejectionRemarks
			const updatedReasons = get(this, "payload.rejectionCodes", {})
			this.rejectionRemarks = updatedRemarks
			if (updatedReasons !== null)
				this.rejectionReasons = [...updatedReasons.map(item => item.code)]
		},
		// Fetch selectable options for Rejection Reason from api
		async readRejectionSettings() {
			const templates = await this.getDynamicTemplate()

			this.rejectionOptions = JSON.parse(
				templates[0]?.dynamicValues[0]?.valueJsonString || "[]"
			).map(item => {
				return {
					name: `${item.code} - ${item.name}`,
					value: item.code
				}
			})
		},
		async sendRejectionEmail(email) {
			const emailTemplates = {
				"S00027-01-000-02": "EMAIL_VHIS_STANDARD_DECLINE_CASE",
				"F00038-01-000-02": "EMAIL_VHIS_FLEXI_SEMIPRIVATE_DECLINE_CASE",
				"F00038-02-000-02": "EMAIL_VHIS_FLEXI_PRIVATE_DECLINE_CASE"
			}
			const plan = this.payload.basket[0].product.productId.plan
			await this.sendNotification({
				input: {
					type: "rejection",
					emailMessage: {
						to: email,
						from: this.env.VHIS_EMAIL_FROM,
						templateRendering: {
							templateId: this.env[emailTemplates[plan]],
							input: {
								name: "data",
								variablesJsonString: JSON.stringify({
									caseWhere1: { id: this.caseId },
									proposalWhere1: { id: this.proposalId },
									offerWhere1: { id: this.offerId }
								})
							}
						}
					}
				}
			})
		},
		async onRejectProposal() {
			// // TODO: Check if reason of rejection has been selected
			// // Change policy status to "REJECTED" or "CANCELLED"
			// // Store reason of rejection in `rejectedReasons`
			this.$emit("startReject")
			this.isLoading = true
			try {
				await this.rejectProposal()
				let contacts = this.case.holder.contacts
				// // 1. Check if client has an email address && if sendEmailToggle is true
				// // 2. Filter contacts array of holder and get all contact with type "email"
				// // 3. Create a new array with values that passed filter
				// // 4. Resolve all promises from array and send rejection email to every email
				if (contacts !== null && this.sendEmailToggle === "yes") {
					const holderEmails = contacts
						.filter(item => {
							if (item.type === "email") return item.value
						})
						.map(item => item.value)
					Promise.all(holderEmails).then(result =>
						result.forEach(item => this.sendRejectionEmail(item))
					)
				}

				// Emit to parent
				this.$emit("rejected")
				// Toast Message
				this.$store.dispatch("addMessage", {
					message: {
						type: "success",
						text: this.$t("rejected")
					}
				})
			} catch (error) {
				console.log(error)
				// Toast Message
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text: this.$t("something_went_wrong")
					}
				})
			}
			this.isLoading = false
			this.$emit("endReject")
		},
		async rejectProposal() {
			return new Promise(async (resolve, reject) => {
				const query = `mutation rejectProposal($caseId: String!, $proposalId: String!, $codes: [String], $remarks: String) {
					rejectProposal(caseId: $caseId, proposalId: $proposalId, codes: $codes, remarks: $remarks) {
						status
						errors
					}
				}`

				const variables = {
					caseId: this.caseId,
					proposalId: this.proposalId,
					codes: this.rejectionReasons,
					remarks: this.rejectionRemarks
				}

				await this.APIRequest(
					"rejectProposal",
					query,
					variables,
					resolve,
					reject
				)
			})
		},
		async getDynamicTemplate() {
			return new Promise(async (resolve, reject) => {
				const query = `query templates($templateWhereInput: templateWhereInput!) {
					templates(where: $templateWhereInput) {
						list {
							...on dynamicTemplate {
								dynamicValues {
									value {
										stringValue
									},
									valueJsonString
								},
								name
							}
						}
					}
				}`

				const variables = {
					templateWhereInput: {
						id: this.env.rejectSettingsTemplateId
					}
				}

				await this.APIRequest("templates", query, variables, resolve, reject)
			})
		},
		sendNotification(variables) {
			return new Promise(async (resolve, reject) => {
				const query = `
          mutation sendNotification($input: sendNotificationInput!) {
            sendNotification(input: $input) {
              errors
              status
            }
          }
        `
				await this.APIRequest(
					"sendNotification",
					query,
					variables,
					resolve,
					reject
				)
			})
		},
		async APIRequest(type, query, variables, resolve, reject) {
			try {
				const { data, errors } = await api({
					query,
					variables
				})

				if (errors) {
					reject(errors)
				} else if (data.errors) {
					reject(data.errors)
				} else {
					resolve(data[type].createdStatus || data[type].list || data[type])
				}
			} catch (error) {
				reject(error)
			}
		}
	},
	mounted() {
		this.initialize()
		this.readRejectionSettings()
	}
}
</script>
